import React, { useState } from 'react';
import { motion, useInView } from 'motion/react';
import { ArrowRight, Menu, X, Pause } from 'lucide-react';

// ─── Image URLs (S3) ───────────────────────────────────────────────────────────
const IMAGES = {
  logo: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-f32cfc5b-fd02-4bf6-a696-340ab4e74611-logo.png',
  hero1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-2660d135-c891-43b4-9a7f-cb976c9960b2-hero1.jpg',
  hero2: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-f01268eb-ef10-4439-9a47-45850a7c33d7-hero2.jpg',
  food1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-5f275207-086e-4b62-8d86-237267148e57-food1.jpg',
  robotTop: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-81f567e4-1313-47f4-ab3b-b39ab80c8d43-robot-top.png',
  robot: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-4df1828f-9de4-4b6c-96ff-0b50533440c6-robot.jpg',
  materials1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-e15c1aa8-3df4-4552-b9ee-c34df5627b74-materials1.jpg',
  pharma1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-e4a4e4a8-98c0-4e16-91d5-93f9e86cbb52-pharma1.jpg',
  industrial1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-8ad104c8-105d-41f4-bb0d-7ca7027e8a46-industrial1.jpg',
  retail1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-5bda7fe6-b41b-4761-a332-0099b51e4ae3-retail1.png',
  retail2: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-66e6eaa2-f0c8-40f3-9f15-6c10aab1ec68-retail2.jpg',
  sectorsBg: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-5a8ecd5e-483e-431b-a9b8-f2c99d8c854e-sectors-bg.png',
};

const BRAND = {
  cream: 'rgb(255,254,251)',
  olive: 'rgb(91,92,48)',
  oliveLight: 'rgb(153,159,86)',
  sand: 'rgb(252,246,232)',
  dark: 'rgb(17,17,17)',
};

// ─── Tile Component ────────────────────────────────────────────────────────────
function Tile({
  bgImage,
  eyebrow,
  title,
  wide = false,
  showPlay = false,
}: {
  bgImage: string;
  eyebrow?: string;
  title?: string;
  wide?: boolean;
  showPlay?: boolean;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
      className={`relative overflow-hidden ${wide ? 'md:col-span-2' : ''}`}
      style={{
        height: 'clamp(340px, 48vw, 686px)',
        borderRadius: 14,
        background: '#000',
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url('${bgImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      {/* Top gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 29%)',
          zIndex: 2,
        }}
      />
      {/* Bottom gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 100%)',
          zIndex: 2,
        }}
      />
      {(eyebrow || title) && (
        <div
          className="absolute z-10"
          style={{
            left: 16,
            bottom: 16,
            right: 16,
            color: '#fff',
            maxWidth: wide ? 1300 : 630,
          }}
        >
          {eyebrow && (
            <div style={{ fontSize: 'clamp(16px, 2.5vw, 27px)', fontWeight: 400, lineHeight: 1, marginBottom: 4 }}>
              {eyebrow}
            </div>
          )}
          {title && (
            <div
              style={{
                fontSize: 'clamp(22px, 3.5vw, 48px)',
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: '-1.656px',
              }}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          )}
        </div>
      )}
      {showPlay && (
        <div
          className="absolute z-10 flex items-center justify-center"
          style={{
            right: 24,
            bottom: 24,
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: BRAND.dark,
          }}
        >
          <div className="flex gap-1">
            <span style={{ display: 'block', width: 4, height: 14, background: '#fff' }} />
            <span style={{ display: 'block', width: 4, height: 14, background: '#fff' }} />
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function Flexivo() {
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks: Array<{ label: string; href: string }> = [
    { label: 'Productos', href: '#productos' },
    { label: 'Sectores', href: '#sectores' },
    { label: 'Novedades', href: '#novedades' },
    { label: 'Contacto', href: '#contacto' },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMenuOpen(false);
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  const sectors = [
    'Alimentos y bebidas',
    'Retail',
    'Farmacéutico',
    'Industrial',
    'Logística',
    'Consumo masivo',
    'Comercio en general',
  ];
  const products = [
    'Termoencogible',
    'Películas plásticas',
    'Empaques flexibles',
    'Stretch film',
    'Bolsas industriales',
    'Materiales de protección',
    'Productos personalizados',
    'Polipropileno moniorientado',
    'Polipropileno biorentado',
  ];
  const footerImages = [
    IMAGES.hero2,
    IMAGES.food1,
    IMAGES.robot,
    IMAGES.materials1,
    IMAGES.pharma1,
    IMAGES.industrial1,
    IMAGES.retail2,
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        background: BRAND.cream,
        fontFamily: "'Noto Sans', sans-serif",
        color: BRAND.dark,
      }}
    >
      {/* Google Font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <style dangerouslySetInnerHTML={{ __html: `
        html { scroll-behavior: smooth; }
        @media (max-width: 1023px) {
          .flexivo-sectors-grid { grid-template-columns: 1fr !important; height: auto !important; }
          .flexivo-sectors-left { padding: 48px 32px !important; }
          .flexivo-sectors-right { padding: 48px 32px !important; }
          .flexivo-sectors-divider { display: none !important; }
        }
        @media (max-width: 767px) {
          .flexivo-hero-grid {
            grid-template-columns: 1fr !important;
            grid-template-rows: 1fr 1fr !important;
            height: calc(100vh - 102px) !important;
            min-height: 480px !important;
            gap: 12px !important;
          }
          .flexivo-hero-card { min-height: 0 !important; height: 100% !important; }
          .flexivo-contact-card { min-height: 0 !important; height: 100% !important; }
          .flexivo-sectors-left { padding: 40px 24px !important; }
          .flexivo-sectors-right { padding: 40px 24px !important; }
          .flexivo-2col { grid-template-columns: 1fr !important; gap: 12px !important; }
          .flexivo-footer { padding: 80px 20px 40px !important; margin-top: 24px !important; }
          .flexivo-footer-image-strip { margin-bottom: 60px !important; }
          .flexivo-footer-strip { grid-template-columns: repeat(3, 1fr) !important; grid-template-rows: repeat(3, 1fr) !important; height: 320px !important; }
          .flexivo-footer-strip > div:nth-child(n+8) { display: none; }
          .flexivo-footer-links { font-size: 15px !important; gap: 12px !important; row-gap: 8px !important; }
          .flexivo-footer-meta { font-size: 13px !important; gap: 10px !important; }
          .flexivo-product-pill { font-size: 15px !important; padding: 0 18px !important; height: 38px !important; }
        }
        @media (max-width: 540px) {
          .flexivo-sectors-right { padding: 32px 20px !important; gap: 24px !important; }
          .flexivo-catalog-pills { gap: 8px !important; }
          .flexivo-product-pill { font-size: 13px !important; padding: 0 14px !important; height: 32px !important; }
        }
        @media (max-width: 400px) {
          .flexivo-sectors-right { padding: 28px 16px !important; gap: 20px !important; }
          .flexivo-catalog-pills { gap: 6px !important; }
          .flexivo-product-pill { font-size: 11.5px !important; padding: 0 11px !important; height: 28px !important; }
          .flexivo-header { padding: 0 20px !important; height: 72px !important; }
          .flexivo-header-logo { height: 30px !important; }
          .flexivo-mobile-menu { top: 72px !important; }
          .flexivo-mobile-menu a { font-size: 17px !important; }
          .flexivo-contact-card-region { height: 56px !important; padding: 0 18px !important; }
          .flexivo-contact-card-region-text { font-size: 18px !important; }
        }
        @media (max-width: 480px) {
          .flexivo-footer-strip { grid-template-columns: repeat(2, 1fr) !important; grid-template-rows: repeat(3, 1fr) !important; height: 360px !important; }
          .flexivo-footer-strip > div:nth-child(n+7) { display: none; }
        }
      ` }} />

      {/* ─── HEADER ─────────────────────────────────────────────────────── */}
      <header
        className="flexivo-header flex items-center justify-between w-full sticky top-0 z-50"
        style={{
          height: 96,
          padding: '0 clamp(20px, 8vw, 240px)',
          background: BRAND.cream,
        }}
      >
        <a href="#top" onClick={(e) => handleNavClick(e, '#top')}>
          <img src={IMAGES.logo} alt="FLEXIVO" className="flexivo-header-logo" style={{ height: 38, width: 'auto' }} />
        </a>
        <nav className="hidden md:flex" style={{ gap: 'clamp(20px, 3.5vw, 48px)' }}>
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="no-underline hover:opacity-70 transition-opacity"
              style={{ color: BRAND.dark, fontSize: 'clamp(15px, 1.4vw, 18px)', fontWeight: 400 }}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <button
          className="md:hidden"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
        >
          {menuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </header>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="flexivo-mobile-menu md:hidden px-6 py-4"
          style={{
            background: BRAND.cream,
            borderTop: '1px solid rgba(0,0,0,0.05)',
            position: 'sticky',
            top: 96,
            zIndex: 49,
          }}
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="block py-3 no-underline"
              style={{ color: BRAND.dark, fontSize: 18 }}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}

      <span id="top" />
      <span id="novedades" />

      {/* ─── MAIN ───────────────────────────────────────────────────────── */}
      <main className="mx-auto" style={{ maxWidth: 1440, padding: '20px 24px 0' }}>
        {/* ─── HERO ROW (fills viewport below header) ────────────────── */}
        <section
          className="flexivo-hero-grid grid gap-5 mb-5"
          style={{
            gridTemplateColumns: 'minmax(0, 921fr) minmax(0, 519fr)',
            height: 'calc(100vh - 126px)',
            minHeight: 360,
          }}
        >
          {/* Hero card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            className="flexivo-hero-card relative overflow-hidden"
            style={{ borderRadius: 14, background: '#000' }}
          >
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url('${IMAGES.hero2}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 30%)',
                zIndex: 2,
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 100%)',
                zIndex: 2,
              }}
            />
            <div
              className="absolute z-10"
              style={{ left: 'clamp(16px, 3vw, 32px)', bottom: 'clamp(16px, 2.5vw, 28px)', right: 'clamp(16px, 3vw, 32px)', color: '#fff', maxWidth: 900 }}
            >
              <div style={{ fontSize: 'clamp(14px, 1.8vw, 24px)', fontWeight: 400, lineHeight: 1.1, marginBottom: 6 }}>
                FLEXIVO · Soluciones Integrales de Empaques
              </div>
              <div
                style={{
                  fontSize: 'clamp(24px, 3vw, 44px)',
                  fontWeight: 700,
                  lineHeight: 1.12,
                  letterSpacing: '-1.2px',
                }}
              >
                Calidad, eficiencia y acompañamiento
                <br className="hidden md:inline" />
                {' '}en cada proyecto
              </div>
            </div>
          </motion.div>

          {/* Contact card */}
          <motion.div
            id="contacto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.25, 0.1, 0.25, 1] }}
            className="flexivo-contact-card flex flex-col overflow-hidden"
            style={{ borderRadius: 14, background: BRAND.sand, scrollMarginTop: 120 }}
          >
            <div className="flex-1 flex items-center justify-center" style={{ padding: '24px 20px' }}>
              <a
                href="https://wa.me/573145551717"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-[10px] no-underline"
              >
                <span
                  style={{
                    color: BRAND.olive,
                    fontWeight: 700,
                    fontSize: 'clamp(22px, 2.6vw, 40px)',
                    letterSpacing: '-1.2px',
                    lineHeight: 1.05,
                  }}
                >
                  Contáctanos hoy
                </span>
                <span
                  className="inline-flex items-center justify-center flex-shrink-0"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: BRAND.olive,
                  }}
                >
                  <ArrowRight size={16} color="#fff" />
                </span>
              </a>
            </div>
            <div
              className="flexivo-contact-card-region flex items-center"
              style={{
                height: 'clamp(64px, 8vw, 96px)',
                background: BRAND.oliveLight,
                border: `1.725px solid ${BRAND.cream}`,
                borderRadius: '0 0 14px 14px',
                padding: '0 clamp(16px, 3vw, 22px)',
              }}
            >
              <span
                className="flexivo-contact-card-region-text"
                style={{
                  color: BRAND.sand,
                  fontWeight: 700,
                  fontSize: 'clamp(20px, 2vw, 27px)',
                }}
              >
                Colombia
              </span>
            </div>
          </motion.div>
        </section>

        {/* ─── ROW 2: Robot + Food ────────────────────────────────────── */}
        <section className="flexivo-2col grid grid-cols-2 gap-5 mb-5">
          <Tile bgImage={IMAGES.robotTop} showPlay />
          <Tile
            bgImage={IMAGES.food1}
            eyebrow="Alimentos y bebidas"
            title="Termoencogible y películas<br>plásticas"
          />
        </section>

        {/* ─── SECTORS + CATALOG ──────────────────────────────────────── */}
        <section
          id="sectores"
          className="flexivo-sectors-grid grid mb-5 overflow-hidden"
          style={{
            gridTemplateColumns: 'minmax(0, 395px) 1px minmax(0, 1fr)',
            minHeight: 'clamp(500px, 51vw, 740px)',
            borderRadius: 14,
            background: BRAND.oliveLight,
            scrollMarginTop: 120,
          }}
        >
          {/* Left: Sectors */}
          <div className="flexivo-sectors-left relative flex flex-col gap-6 justify-center" style={{ padding: '65px 63px 159px' }}>
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), url('${IMAGES.sectorsBg}') center/cover no-repeat`,
              }}
            />
            <div className="relative z-10">
              <div
                style={{
                  fontWeight: 500,
                  fontSize: 20,
                  letterSpacing: '2.863px',
                  lineHeight: '30.673px',
                  color: 'rgba(255,251,240,0.5)',
                }}
              >
                INDUSTRIAS
              </div>
              <div
                style={{
                  fontSize: 'clamp(28px, 3.5vw, 47.7px)',
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: '-0.852px',
                  color: '#fff',
                  whiteSpace: 'pre-line',
                  marginTop: 16,
                }}
              >
                {'Sectores que\natendemos'}
              </div>
              <div className="flex flex-col mt-6">
                {sectors.map((s, i) => (
                  <a
                    key={s}
                    href="#"
                    className="block no-underline"
                    style={{
                      padding: '6px 0',
                      color: '#fff',
                      fontSize: 'clamp(16px, 2vw, 24px)',
                      fontWeight: i === 0 ? 700 : 400,
                    }}
                  >
                    {s}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="flexivo-sectors-divider" style={{ background: 'rgba(255,255,255,0.1)' }} />

          {/* Right: Products */}
          <div
            id="productos"
            className="flexivo-sectors-right relative flex flex-col gap-[41px] justify-center"
            style={{ padding: '0 clamp(32px, 6vw, 81px)', scrollMarginTop: 120 }}
          >
            <div
              style={{
                fontWeight: 500,
                fontSize: 20,
                letterSpacing: '2.863px',
                color: 'rgba(252,246,232,0.5)',
              }}
            >
              CATÁLOGO
            </div>
            <div
              style={{
                fontSize: 'clamp(28px, 3.5vw, 47.7px)',
                fontWeight: 700,
                lineHeight: 1.15,
                color: '#fff',
                whiteSpace: 'pre-line',
              }}
            >
              {'Productos que\nofrecemos'}
            </div>
            <div className="flexivo-catalog-pills flex flex-wrap gap-3" style={{ maxWidth: 800 }}>
              {products.map((p) => (
                <a
                  key={p}
                  href="#"
                  className="flexivo-product-pill inline-flex items-center no-underline transition-colors hover:text-[rgb(91,92,48)]"
                  style={{
                    height: 50,
                    padding: '0 35px',
                    borderRadius: 200,
                    border: '1px solid #fff',
                    color: '#fff',
                    fontSize: 'clamp(14px, 1.8vw, 23.86px)',
                    whiteSpace: 'nowrap',
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.background = '#fff';
                    (e.target as HTMLElement).style.color = BRAND.olive;
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.background = 'transparent';
                    (e.target as HTMLElement).style.color = '#fff';
                  }}
                >
                  {p}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ─── ROW 4: Pharma + Industrial ─────────────────────────────── */}
        <section className="flexivo-2col grid grid-cols-2 gap-5 mb-5">
          <Tile
            bgImage={IMAGES.pharma1}
            eyebrow="Sector farmacéutico"
            title="Precisión y protección<br>en cada entrega"
          />
          <Tile
            bgImage={IMAGES.industrial1}
            eyebrow="Sector industrial"
            title="Empaques para<br>almacenamiento y logística"
          />
        </section>

        {/* ─── ROW 5: Wide Materials ──────────────────────────────────── */}
        <section className="flexivo-2col grid grid-cols-2 gap-5 mb-5">
          <Tile
            bgImage={IMAGES.materials1}
            eyebrow="Polipropileno MOPP y BOPP"
            title="Materiales de protección de alto desempeño"
            wide
          />
        </section>

        {/* ─── ROW 6: Retail ──────────────────────────────────────────── */}
        <section className="flexivo-2col grid grid-cols-2 gap-5 mb-5">
          <Tile
            bgImage={IMAGES.retail1}
            eyebrow="Retail y consumo masivo"
            title="Bolsas industriales y<br>empaques flexibles"
            showPlay
          />
          <Tile
            bgImage={IMAGES.retail2}
            eyebrow="Sector retail y consumo"
            title="Empaques para<br>consumo masivo"
          />
        </section>
      </main>

      {/* ─── FOOTER ───────────────────────────────────────────────────── */}
      <footer
        className="flexivo-footer relative"
        style={{
          background: '#000',
          color: '#fff',
          padding: '140px 24px 60px',
          marginTop: 40,
        }}
      >
        {/* Image strip */}
        <div
          className="flexivo-footer-image-strip mx-auto relative overflow-hidden"
          style={{ maxWidth: 1232, marginBottom: 110, height: 'clamp(220px, 34vw, 497px)' }}
        >
          <div className="flexivo-footer-strip grid h-full" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 7 }}>
            {footerImages.map((img, i) => (
              <div
                key={i}
                style={{
                  backgroundImage: `url('${img}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: '#222',
                }}
              />
            ))}
          </div>
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'rgba(17,17,17,0.3)' }}
          />
        </div>

        {/* Footer links */}
        <div
          className="flexivo-footer-links flex flex-wrap justify-center items-center gap-6 mb-4"
          style={{ fontSize: 24 }}
        >
          {[
            'Asesoría Técnica',
            'Acerca de FLEXIVO',
            'Sectores',
            'Productos',
            'Responsabilidad Social',
            'Preguntas Frecuentes',
          ].map((link, i, arr) => (
            <React.Fragment key={link}>
              <a href="#" className="no-underline" style={{ color: '#fff' }}>
                {link}
              </a>
              {i < arr.length - 1 && (
                <span style={{ opacity: 0.6, fontSize: 18 }}>•</span>
              )}
            </React.Fragment>
          ))}
        </div>

        <div
          className="flexivo-footer-meta flex flex-wrap justify-center items-center gap-5 mb-3.5 px-4"
          style={{ fontSize: 'clamp(13px, 1.5vw, 18px)', color: '#fff', opacity: 0.6 }}
        >
          {[
            'Política de Privacidad',
            'Términos y Condiciones',
            'Tel: +57 314 555 17 17',
            'flexivosas@gmail.com',
            'Accesibilidad',
          ].map((item, i, arr) => (
            <React.Fragment key={item}>
              <span>{item}</span>
              {i < arr.length - 1 && <span>•</span>}
            </React.Fragment>
          ))}
        </div>

        <div className="text-center" style={{ fontSize: 18, color: '#fff', opacity: 0.6 }}>
          © FLEXIVO S.A.S. 2024 - 2025
        </div>
      </footer>
    </div>
  );
}
